import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../shell/backofficeSidebar';
import { useAuth } from '../../../../context/AuthContext';
import { formatOrderReference } from '../../../../utils/formatOrderReference';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type FeedbackRow = {
  id: string;
  customerEmail: string;
  customerName?: string | null;
  customerPhone?: string | null;
  orderId?: string | null;
  type: 'FEEDBACK' | 'COMPLAINT';
  status: 'NEW' | 'IN_REVIEW' | 'RESOLVED' | 'REJECTED';
  subject?: string | null;
  message: string;
  resolutionNote?: string | null;
  resolvedAt?: string | null;
  createdAt: string;
};

export default function WorkspaceFeedbackInboxPage() {
  const location = useLocation();
  const { t } = useTranslation();
  const { authorizedFetch } = useAuth();
  const globalAuthorizedFetch = React.useCallback(
    (input: RequestInfo | URL, init: RequestInit = {}) => {
      const headers = new Headers(init.headers || {});
      headers.set('x-department-slug', '');
      return authorizedFetch(input, { ...init, headers });
    },
    [authorizedFetch],
  );

  const [rows, setRows] = React.useState<FeedbackRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [savingId, setSavingId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [scopeFilter, setScopeFilter] = React.useState<'ACTIVE' | 'ARCHIVED' | 'ALL'>('ACTIVE');
  const [statusFilter, setStatusFilter] = React.useState<'ALL' | FeedbackRow['status']>('ALL');
  const [typeFilter, setTypeFilter] = React.useState<'ALL' | FeedbackRow['type']>('ALL');
  const [q, setQ] = React.useState('');
  const [resolutionDrafts, setResolutionDrafts] = React.useState<Record<string, string>>({});

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = new URL(`${API_BASE}/customer/feedback/inbox`);
      if (scopeFilter !== 'ALL') url.searchParams.set('scope', scopeFilter);
      if (statusFilter !== 'ALL') url.searchParams.set('status', statusFilter);
      if (typeFilter !== 'ALL') url.searchParams.set('type', typeFilter);
      if (q.trim()) url.searchParams.set('q', q.trim());
      const res = await globalAuthorizedFetch(url.toString());
      if (!res.ok) {
        throw new Error(
          t('feedback_inbox_load_failed', {
            defaultValue: 'Failed to load feedback inbox',
          }),
        );
      }
      const data = (await res.json()) as FeedbackRow[];
      setRows(data);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : t('feedback_inbox_load_failed', {
              defaultValue: 'Failed to load feedback inbox',
            }),
      );
    } finally {
      setLoading(false);
    }
  }, [globalAuthorizedFetch, q, scopeFilter, statusFilter, t, typeFilter]);

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      void load();
    }, 250);
    return () => window.clearTimeout(timer);
  }, [load]);

  const updateStatus = async (id: string, status: FeedbackRow['status']) => {
    setSavingId(id);
    setError(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/customer/feedback/${id}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status,
          resolutionNote: resolutionDrafts[id] || undefined,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
          (body as { message?: string }).message ||
            t('feedback_inbox_update_failed', {
              defaultValue: 'Failed to update status',
            }),
        );
      }
      await load();
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : t('feedback_inbox_update_failed', {
              defaultValue: 'Failed to update status',
            }),
      );
    } finally {
      setSavingId(null);
    }
  };

  return (
    <MainLayout withSidebar sidebarItems={getBackofficeSidebarItems(t)} activePath={location.pathname}>
      <main className="workspace-page">
        <section>
          <h1 className="workspace-page-title">
            {t('feedback_inbox_title', { defaultValue: 'Feedback inbox' })}
          </h1>
          <p className="workspace-page-subtitle text-slate-600">
            {t('feedback_inbox_subtitle', {
              defaultValue:
                'Review customer feedback and complaints, then resolve them with notes.',
            })}
          </p>
        </section>

        <section className="workspace-stat-card">
          <div className="grid gap-2 sm:grid-cols-4">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('feedback_inbox_search_placeholder', {
                defaultValue: 'Search subject, message, email, order...',
              })}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm sm:col-span-2"
            />
            <select
              value={scopeFilter}
              onChange={(e) => {
                const next = (e.target.value as 'ACTIVE' | 'ARCHIVED' | 'ALL') || 'ACTIVE';
                setScopeFilter(next);
                setStatusFilter('ALL');
              }}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="ACTIVE">
                {t('feedback_inbox_scope_active', { defaultValue: 'Active (default)' })}
              </option>
              <option value="ARCHIVED">
                {t('feedback_inbox_scope_archived', { defaultValue: 'Archived' })}
              </option>
              <option value="ALL">{t('common.all', { defaultValue: 'All' })}</option>
            </select>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter((e.target.value as any) || 'ALL')}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="ALL">
                {t('feedback_inbox_all_types', { defaultValue: 'All types' })}
              </option>
              <option value="FEEDBACK">
                {t('feedback_inbox_type_feedback', { defaultValue: 'Feedback' })}
              </option>
              <option value="COMPLAINT">
                {t('feedback_inbox_type_complaint', { defaultValue: 'Complaint' })}
              </option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter((e.target.value as any) || 'ALL')}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="ALL">
                {t('feedback_inbox_all_status', { defaultValue: 'All status' })}
              </option>
              {scopeFilter !== 'ARCHIVED' ? <option value="NEW">NEW</option> : null}
              {scopeFilter !== 'ARCHIVED' ? <option value="IN_REVIEW">IN_REVIEW</option> : null}
              {scopeFilter !== 'ACTIVE' ? <option value="RESOLVED">RESOLVED</option> : null}
              {scopeFilter !== 'ACTIVE' ? <option value="REJECTED">REJECTED</option> : null}
            </select>
          </div>
        </section>

        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        ) : null}

        <section className="space-y-3">
          {loading ? (
            <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
              {t('common.loading', { defaultValue: 'Loading...' })}
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
              {t('feedback_inbox_empty', { defaultValue: 'No feedback found.' })}
            </div>
          ) : (
            rows.map((r) => (
              <article key={r.id} className="workspace-stat-card">
                {/** Closed tickets are archived and read-only. */}
                {(() => {
                  const isArchived = r.status === 'RESOLVED' || r.status === 'REJECTED';
                  return (
                    <>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className={`rounded-full px-2 py-0.5 font-semibold ${r.type === 'COMPLAINT' ? 'bg-rose-100 text-rose-800' : 'bg-slate-100 text-slate-800'}`}>
                    {r.type}
                  </span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-800">{r.status}</span>
                  {isArchived ? (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-800">
                      {t('feedback_inbox_archived', { defaultValue: 'ARCHIVED' })}
                    </span>
                  ) : null}
                  <span className="text-slate-500">{new Date(r.createdAt).toLocaleString()}</span>
                </div>
                <h3 className="mt-2 text-sm font-semibold text-slate-900">
                  {r.subject ||
                    t('feedback_inbox_no_subject', { defaultValue: '(No subject)' })}
                </h3>
                <p className="mt-1 text-xs text-slate-600">
                  {r.customerName || r.customerEmail}
                  {r.customerPhone ? ` · ${r.customerPhone}` : ''}
                  {r.orderId ? ` · ${formatOrderReference(r.orderId)}` : ''}
                </p>
                <p className="mt-2 whitespace-pre-wrap text-sm text-slate-800">{r.message}</p>

                {isArchived ? (
                  r.resolutionNote ? (
                    <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                      <p className="font-semibold">
                        {t('feedback_inbox_resolution_note', {
                          defaultValue: 'Resolution note',
                        })}
                      </p>
                      <p className="mt-1 whitespace-pre-wrap">{r.resolutionNote}</p>
                    </div>
                  ) : (
                    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                      {t('feedback_inbox_archived_readonly', {
                        defaultValue: 'Archived: this item can no longer be edited.',
                      })}
                    </div>
                  )
                ) : (
                  <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_auto]">
                    <textarea
                      rows={2}
                      placeholder={t('feedback_inbox_resolution_optional', {
                        defaultValue: 'Resolution note (optional)',
                      })}
                      value={resolutionDrafts[r.id] ?? (r.resolutionNote || '')}
                      onChange={(e) =>
                        setResolutionDrafts((prev) => ({
                          ...prev,
                          [r.id]: e.target.value,
                        }))
                      }
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                    <div className="flex flex-wrap gap-2 sm:flex-col">
                      {(['NEW', 'IN_REVIEW', 'RESOLVED', 'REJECTED'] as FeedbackRow['status'][]).map((s) => (
                        <button
                          key={s}
                          type="button"
                          disabled={savingId === r.id || s === r.status}
                          onClick={() => void updateStatus(r.id, s)}
                          className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-800 disabled:opacity-50"
                        >
                          {t('feedback_inbox_mark_status', {
                            defaultValue: 'Mark {{status}}',
                            status: s,
                          })}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                    </>
                  );
                })()}
              </article>
            ))
          )}
        </section>
      </main>
    </MainLayout>
  );
}

